/**
 * kartDurumuService.js — Giriş (Kurulum/Söküm) kartı rozetinin üç hali.
 *
 * Backend her kadro satırında `kartDurumu` döndürüyor (2026-09-15, FirmaPersonelDTO):
 *  · KART_ALABILIR            — yeşil: kart çıkabilir (`cardEligible` ile aynı).
 *  · EVRAK_TAMAM_KART_ALAMAZ  — sarı: kişinin kendi belgeleri tamam (ya da Özel İzin muaf
 *                               tutuyor) ama yasak, KVKK, kimlik doğrulama veya firmanın üç
 *                               İSG belgesi engelliyor.
 *  · EVRAK_EKSIK              — kırmızı: mesleğin istediği bir belge eksik, reddedilmiş,
 *                               süresi dolmuş, fuar için fazla eski ya da HÂLÂ ONAY BEKLİYOR.
 *
 * Hangi engelin hangi renge düştüğü backend'de (KartDurumuConstant) — burada yeniden
 * hesaplanmaz. ⚠️ Firma belgeleri eksikliği şimdilik SARI (geçici karar, doküman kontrol
 * özelliği netleştirecek); değişirse backend'de tek satır değişir, bu dosya değişmez.
 *
 * `kartDurumu`, `cardEligible`ın hesaplanmadığı yerde null gelir (firmaId'siz kişi dizini) —
 * null'ı kırmızı diye göstermeyin. Bu dosyanın tüketicileri yalnızca kadro uçlarını
 * (`/personnel/firma/{firmaId}`) okuyor, orada her zaman dolu.
 */

import i18n from "../i18n";

export const KART_DURUMU = {
  KART_ALABILIR: { color: 'success', get label() { return i18n.t("services:kartDurumuService.kartAlabilir"); } },
  EVRAK_TAMAM_KART_ALAMAZ: { color: 'warning', get label() { return i18n.t("services:kartDurumuService.evrakTamamKartAlamaz"); } },
  EVRAK_EKSIK: { color: 'danger', get label() { return i18n.t("services:kartDurumuService.evrakEksikKartAlamaz"); } },
};

// #539: the company portal names the three card decisions by authorisation — green "Authorized", yellow "Partially Authorized", red
// "Unauthorized" — while the staff pages keep the card wording above. Same colours and keys, only the label differs, so callers that
// draw for the portal pass `{ portal: true }`; the staff Kişi Listesi / Firma Detay lists never do.
const PORTAL_ETIKETLERI = {
  success: () => i18n.t("portal:personel.kartYetkili"),
  warning: () => i18n.t("portal:personel.kartKismenYetkili"),
  danger: () => i18n.t("portal:personel.kartYetkisiz"),
};
/** Portal label of a decision colour (success / warning / danger); `undefined` for the neutral gray "unknown". */
export const portalKartEtiketi = (color) => PORTAL_ETIKETLERI[color]?.();
const portalEtiketli = (meta, portal) => (portal && PORTAL_ETIKETLERI[meta.color] ? { ...meta, label: PORTAL_ETIKETLERI[meta.color]() } : meta);

/**
 * Bir kadro satırının rozet bilgisi: `{ key, color, label }`.
 *
 * `kartDurumu` yoksa (mock modu — firmaProfileService'teki mock kadro üç durumu bilmiyor)
 * eski iki durumlu `cardEligible`'a düşer; o durumda `key` yeşil için KART_ALABILIR,
 * kırmızı için null. `portal`: etiketler firma portalının "Yetkili" adlarıyla (#539).
 */
export function kartDurumuMeta(record, { portal = false, kkd = record?.kkd } = {}) {
  const key = record?.kartDurumu;
  // #374: a green row whose fair asks for a BLOCKING PPE item the person lacks is yellow ("Alanda Kart Alabilir" / portal "Partially
  // Authorized"), exactly as on the Kişi Listesi (kartKarariUc). Backend 'ALANDA_KART_ALABILIR' (backlog #532) is read as is.
  const yesil = key === 'KART_ALABILIR' || (!key && Boolean(record?.cardEligible));
  if (key === 'ALANDA_KART_ALABILIR' || (yesil && kkdEngelliyor(kkd))) {
    return portalEtiketli({ key: 'ALANDA_KART_ALABILIR', ...KART_KARARI_UC.ALANDA_KART_ALABILIR }, portal);
  }
  if (key && KART_DURUMU[key]) return portalEtiketli({ key, ...KART_DURUMU[key] }, portal);
  return portalEtiketli(
    record?.cardEligible
      ? { key: 'KART_ALABILIR', ...KART_DURUMU.KART_ALABILIR }
      : { key: null, color: 'danger', label: i18n.t("services:kartDurumuService.kartAlamaz") },
    portal,
  );
}

/**
 * Kişi Listesi satırı için karar rozeti: `{ key, color, label, hesaplandi }`.
 *
 * `kartDurumuMeta`dan FARKI: `kartDurumu` yoksa `cardEligible`a DÜŞMEZ. Kişi dizininde
 * (`/personnel/kisi-listesi`) karar (kişi, firma, fuar) üçlüsü içindir — "Tüm kişiler"de ya da
 * firması olmayan kişide hesaplanmaz ve `cardEligible: false` "kart alamaz" DEMEZ. Oradan
 * `kartDurumuMeta` çağırmak 38.786 hiç görevlendirilmemiş kişiyi kırmızı basardı. Hesaplanmadıysa
 * nötr gri "bilinmiyor" döner (`color: 'secondary'`; hex'e `statusBackground` çevirir).
 */
export function kartKararMeta(record) {
  const key = record?.kartDurumu;
  if (key && KART_DURUMU[key]) return { key, ...KART_DURUMU[key], hesaplandi: true };
  return { key: null, color: 'secondary', label: i18n.t("services:kartDurumuService.kartDurumuBilinmiyor"), hesaplandi: false };
}

// WAS (until #531): KART_LACIVERT / KART_SEVIYESI / kartSeviyesiMeta — the Kişi Listesi corner ribbon told HOW a green row got its card (backend #361
// `kartSeviyesi`: lacivert "Kart: Tam" / green "Yeterli" / green "Özel İzin"). The ribbon now only marks a granted Özel İzin and the frame is a plain
// green / yellow / red, so the level is drawn nowhere; the `kartSeviyesi` list filter and `kartSeviyesi` on the row (greenest-firma tie-break) stay.

/**
 * #531 — Kişi Listesi'nin ÜÇ durumlu kart kararı (vesikalık çerçevesi + Kart Yazdır düğmesi aynı renk):
 *   · KART_ALABILIR        — yeşil: kart çıkabilir.
 *   · ALANDA_KART_ALABILIR — sarı: KVKK ve KKD dışında her şart tamam; ikisi de ALANDA yapılır.
 *   · KART_ALAMAZ          — kırmızı: yasak, kimlik doğrulanmamış, firma reddetti, firma evrakı ya da kişisel belge eksik.
 * Backend bugün iki sarıyı birbirinden ayırmıyor (EVRAK_TAMAM_KART_ALAMAZ yasak / kimlik / firma / KVKK'nın hepsini kapsar)
 * ve KKD'yi hiç kapı saymaz. Backend `kartDurumu: 'ALANDA_KART_ALABILIR'` verene kadar (backlog #532) üçüncü durum BURADA,
 * satırın `uygunsuzluklar[]` kodlarından türetilir; backend değeri geldiğinde o kazanır, türetme kendiliğinden devre dışı kalır.
 * Karar `null`/bilinmiyorsa (Tüm Kişiler: fuarsız satırda karar hesaplanmaz) `{ key: null, color: 'secondary', hesaplandi: false }`.
 */
export const KART_KARARI_UC = {
  KART_ALABILIR: { color: 'success', get label() { return i18n.t("services:kartDurumuService.kartAlabilir"); } },
  ALANDA_KART_ALABILIR: { color: 'warning', get label() { return i18n.t("services:kartDurumuService.alandaKartAlabilir"); } },
  KART_ALAMAZ: { color: 'danger', get label() { return i18n.t("services:kartDurumuService.kartAlamaz"); } },
};

/** Küçük = daha yeşil; `onFirmaSec` en yeşil firmayı bununla seçer. */
export const KART_KARARI_UC_SIRASI = { KART_ALABILIR: 0, ALANDA_KART_ALABILIR: 1, KART_ALAMAZ: 2 };

/**
 * #374: satırın KKD'sinde fuarın istediği bir ENGELLEYİCİ madde eksik mi? `durum: 'EKSIK'` tam olarak bunu söyler; `UYARI` (uyarı düzeyindeki
 * madde eksik, kişi hâlâ KKD uygun), `UYGUN`, `ISTENMIYOR` (fuar KKD istemiyor) ve `null` (fuarsız satır) hayır. Kart kararını sarıya
 * çeviren tek koşul — Kişi Listesi, firma listeleri, Anasayfa ve Personel Detayı aynı yerden okur.
 */
export function kkdEngelliyor(kkd) {
  return kkd?.istenen !== false && kkd?.durum === 'EKSIK';
}

// Özel İzin'in kaldırmadığı engeller (backend `cardEligible`: YASAKLI, FIRMA_REDDETTI, KVKK, KIMLIK; belge ve firma evrakı izinle düşer).
const IZINLE_DUSMEYEN_ENGELLER = ['YASAKLI', 'FIRMA_REDDETTI', 'KIMLIK_DOGRULAMA'];

/**
 * Satırda KVKK dışında kartı engelleyen bir şey kaldı mı? `uygunsuzluklar[]` Özel İzin sahibi için izinle düşen belge/firma
 * kodlarını da listeler (backend PersonnelService.yolKarari yorumu) — o yüzden izin varsa yalnızca düşmeyenlere bakılır.
 */
function yalnizKvkkEngelli(satir, ozelIzin) {
  const kodlar = satir?.uygunsuzluklar;
  if (!Array.isArray(kodlar) || !kodlar.includes('KVKK')) return false;
  const kalan = kodlar.filter((k) => k !== 'KVKK');
  return (ozelIzin ? kalan.filter((k) => IZINLE_DUSMEYEN_ENGELLER.includes(k)) : kalan).length === 0;
}

/**
 * @param satir  kisi-listesi satırı YA DA `firmalar[]` girdisi (ikisi de `kartDurumu` + `uygunsuzluklar[]` taşır)
 * @param opts.ozelIzin  satırın aktif Özel İzni (`singlePermit`) — firma girdisinde yok, satırdan verilir
 * @param opts.kkd       satırın `kkd` nesnesi; `durum: 'EKSIK'` (istenen bir ENGELLEYİCİ madde yok) yeşili sarıya çevirir.
 *                       `UYARI` (uyarı düzeyindeki madde) çevirmez; `ISTENMIYOR` / `null` yok sayılır.
 * @param opts.portal    #539: firma portalı — etiketler "Yetkili / Kısmen Yetkili / Yetkisiz" (renk ve anahtar aynı)
 * @returns { key, color, label, hesaplandi }
 */
export function kartKarariUc(satir, { ozelIzin = null, kkd = null, portal = false } = {}) {
  const durum = satir?.kartDurumu;
  if (!durum || !(KART_DURUMU[durum] || durum === 'ALANDA_KART_ALABILIR')) {
    return { key: null, color: 'secondary', label: i18n.t("services:kartDurumuService.kartDurumuBilinmiyor"), hesaplandi: false };
  }
  const al = (key) => portalEtiketli({ key, ...KART_KARARI_UC[key], hesaplandi: true }, portal);
  if (durum === 'ALANDA_KART_ALABILIR') return al('ALANDA_KART_ALABILIR');
  if (durum === 'KART_ALABILIR') return al(kkdEngelliyor(kkd) ? 'ALANDA_KART_ALABILIR' : 'KART_ALABILIR');
  return al(yalnizKvkkEngelli(satir, ozelIzin) ? 'ALANDA_KART_ALABILIR' : 'KART_ALAMAZ');
}

/**
 * #531 — kişinin BİR firmadaki durumu, rol rozetinin rengi. Fuar = üst çubuk fuarı (backend `aktifFuarda` /
 * `aktifFuarEslesmeDurumu`, yalnızca staff'a gelir):
 *   · 0 → sarı  "Eşlendi"         (SAKA alanda eşledi, firmanın cevabı bekleniyor)
 *   · 1 ya da kayıtsız (eski görevlendirme) → yeşil "Görevlendirildi" (firma kendi atadı ya da eşlemeyi onayladı)
 *   · 2 → kırmızı "Firma reddetti"
 *   · aktifFuarda değil / alan yok → nötr gri "Bu fuarda görevli değil"
 * Onaylanmış eşleme ile doğrudan görevlendirme backend'de aynı değerdir (`eslesmeTipi` listeye gelmiyor): ikisi de yeşil.
 */
export function firmaKatilimMeta(firma) {
  if (firma?.aktifFuarda !== true) {
    return { key: 'YOK', color: 'secondary', label: i18n.t("services:kartDurumuService.fuardaGorevliDegil") };
  }
  const d = firma.aktifFuarEslesmeDurumu;
  if (d === 0) return { key: 'ESLENDI', color: 'warning', label: i18n.t("services:kartDurumuService.eslendi") };
  if (d === 2) return { key: 'REDDEDILDI', color: 'danger', label: i18n.t("services:kartDurumuService.firmaReddetti") };
  return { key: 'GOREVLENDIRILDI', color: 'success', label: i18n.t("services:kartDurumuService.gorevlendirildi") };
}

/**
 * Kararın hangi yoldan geldiği (backend #125, `kartYolu`): `CALISAN` ya da `ISVEREN`. İşveren
 * (`person_is_owner`) iki yola birden sahip: işçi yolu + (kişisel belge yok, firmanın FIRMA grubu
 * + KVKK) işveren yolu; kart ikisinden biri uyunca çıkar. `uygunsuzluklar[]` DAİMA kararın geldiği
 * yolun listesidir — uygun olmasa da yakın olan yol seçilir — o yüzden ipucu `cardEligibleReason`a
 * değil `kartYolu`na bakar: kart çıkmıyorken de "bu liste işveren yolunun" diyebilmek gerekiyor.
 * `kartYolu` yalnızca karar hesaplandıysa gelir (Tüm kişiler'de yok → false).
 */
export const ISVEREN_ETIKETI = 'İşveren olarak';
// #334: the same label in the UI language, read at render (the constant above stays for staff pages).
export const isverenEtiketi = () => i18n.t("services:kartDurumuService.isverenOlarak");
// #358: rol rozetinin "İşçi" etiketi "Çalışan" oldu (iç anahtar 'isci' kalır). Denetmen sayfalarının tek ortak etiketi;
// firma portalı aynı kelimeyi t("portal:personel.isci") üzerinden okur.
export const CALISAN_ETIKETI = 'Çalışan';

export function isverenYolu(record) {
  return record?.kartYolu === 'ISVEREN';
}
